import Alert from 'react-s-alert';
import React from 'react';
import PropTypes from 'prop-types';

import ExtendedTextPlainText from './plainText/ExtendedTextPlainText';
import ExtendedTextCkeditor from './ckeditor/ExtendedTextCkeditor';
import ExtendedTextCode from './code/ExtendedTextCodeInteraction';
import sessionReader from '../../../../store/readHelpers/SessionReader';
import settingsReader from '../../../../store/readHelpers/SettingsReader';
import storeManager from '../../../../store/StoreManager';
import { reportError } from '../../../../services/ErrorReporting';
import alertService from '../../../../services/AlertService';
import { t } from '../../../../services/i18nService';
import responseService from '../../../../services/ResponseService';
import Prompt from '../Prompt';
import KeyboardHelpAndPrompt from '../../keyboardHelp/KeyboardHelpAndPrompt';
import wordCountService from '../../../../services/WordCountService';
import questionReader from '../../../../store/readHelpers/QuestionReader';
import extendedTextA11ySelector from '../../../../selectors/question/interactions/extendedTextInteraction/ExtendedTextA11ySelector';

const AUTO_SAVE_INTERVAL = 15 * 1000;

function getUserData(userResponse) {
    return (Array.isArray(userResponse) && userResponse.length > 0) ? userResponse[0] : '';
}

export function isCodeInteraction(model) {
    return model.variant === 'code' || model.variant === 'code-compiler';
}

export function isRichTextInteraction(model) {
    return model.format !== 'plain' && !isCodeInteraction(model);
}

class ExtendedTextInteraction extends React.Component {
    static propTypes = {
        callback: PropTypes.func.isRequired, // callback(responseIdentifier, [user answer])
        context: PropTypes.string,
        handleInteractionReady: PropTypes.func.isRequired,
        isPreRender: PropTypes.bool,
        isReadOnly: PropTypes.bool,
        model: PropTypes.object.isRequired, // model taken from responseModels related to this interaction
        questionModel: PropTypes.object.isRequired,
        services: PropTypes.object.isRequired,
        userResponse: PropTypes.array.isRequired, // array containing the user answer as the only entry
        extraResponses: PropTypes.array,
        correctResponses: PropTypes.array,
        viewMedia: PropTypes.string.isRequired,
        defaultValue: PropTypes.array,
        settingValues: PropTypes.object.isRequired,
    };

    constructor(props) {
        super(props);
        this.currentErrorId = undefined;
    }

    componentDidMount() {
        this.initAutoSaver();

        const { model, handleInteractionReady } = this.props;
        if (this.shouldHideInteractionsNotUsefulOnPaper() && model.type !== 'plain') {
            handleInteractionReady(true);
        }
    }

    componentDidUpdate() {
        this.initAutoSaver();
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillReceiveProps(nextProps) {
        // question was reset
        if (nextProps.userResponse.length === 0 && this.props.userResponse.length !== 0) {
            if (this.editor
                && this.editor.isEditorInitialized()
                && typeof this.editor.resetEditor === 'function') {
                const prevLength = `Previous user response length: ${this.props.userResponse.length}`;
                console.log(`Reset ckeditor value because of empty new user response, ${this.props.questionModel.contentRevisionId}. ${prevLength}`);
                this.editor.resetEditor();
            }
        }
    }

    componentWillUnmount() {
        if (this.currentErrorId) {
            Alert.close(this.currentErrorId);
            this.currentErrorId = undefined;
        }
        clearInterval(this.autoSaveTimer);
        this.autoSave(true);
    }

    autoSave = (secondCheckForDirty = false) => {
        const { model, questionModel, callback, isPreRender } = this.props;

        if (!secondCheckForDirty && !this.editor.isEditorInitialized()) {
            // secondCheckForDirty is true only on componentWillUnmount. The editor might not be
            // initialized at this point on rapid navigation
            if (!isPreRender) {
                reportError({
                    message: 'Editor is not initialized on auto save',
                    id: questionModel.contentRevisionId,
                });
            }
            if (!this.currentErrorId && !isPreRender) {
                this.currentErrorId = alertService.showError(t('alert.extendedText.autoSaveEditorNotInitialized'), () => {
                    this.currentErrorId = undefined;
                });
            }
            return;
        }

        if (this.editor.isDirty() || (secondCheckForDirty && this.isEditorDirtyForReal())) {
            const editorData = this.editor.getEditorData();
            callback(model.identifier, [editorData]);

            // For AceEditor, at least
            if (typeof this.editor.resetDirty === 'function') {
                this.editor.resetDirty(false);
            }
        }
    };

    initAutoSaver() {
        if (this.autoSaveTimer || this.props.isReadOnly || this.props.isPreRender) {
            return;
        }

        this.autoSaveTimer = setInterval(() => {
            this.autoSave();
        }, AUTO_SAVE_INTERVAL);
        responseService.subscribeToItemResultUpdates(this.props.questionModel.contentRevisionId, this.autoSave);
    }

    isEditorDirtyForReal = () => {
        let isEditorDirtyForReal = false;
        const userResponseText = this.props.userResponse.length > 0 ? this.props.userResponse[0] : '';

        if (this.editor) {
            isEditorDirtyForReal = this.editor.getEditorData() !== userResponseText;
        }

        return isEditorDirtyForReal;
    };

    shouldHideInteractionsNotUsefulOnPaper() {
        return settingsReader.hasSettingValue(sessionReader.printParamNames.HIDE_INTERACTIONS_NOT_USEFUL_ON_PAPER, true);
    }

    shouldSaveOnEmptyChange() {
        const { questionModel } = this.props;
        return questionReader.shouldCommitAnswerTimestamp(questionModel, true);
    }

    renderExtendedTextPlainText() {
        const { handleInteractionReady, isReadOnly, model, questionModel, userResponse, viewMedia } = this.props;

        return (
            <ExtendedTextPlainText
                autoExpand={model.autoExpand}
                autoSave={this.autoSave}
                contentRevisionId={questionModel.contentRevisionId}
                expectedLines={model.expectedLines}
                fieldWidth={model.fieldWidth}
                handleInteractionReady={handleInteractionReady}
                identifier={model.identifier}
                isReadOnly={isReadOnly}
                maxWords={model.maxWords}
                shouldSaveOnEmptyChange={this.shouldSaveOnEmptyChange()}
                showWordCount={wordCountService.shouldShowWordCount(model.showWordCount)}
                userData={getUserData(userResponse)}
                viewMedia={viewMedia}
                ref={(component) => {
                    this.editor = component ? component.getWrappedInstance() : undefined;
                }}
            />
        );
    }

    renderExtendedTextCode(variant) {
        const {
            handleInteractionReady,
            isPreRender,
            isReadOnly,
            model,
            questionModel,
            userResponse,
            extraResponses,
            services,
            context,
            viewMedia,
            correctResponses,
            defaultValue,
        } = this.props;
        const isViewMediaPrint = sessionReader.isViewMediaPrint();
        const userData = getUserData(userResponse);

        if (this.shouldHideInteractionsNotUsefulOnPaper()) {
            return null;
        }

        return (
            <ExtendedTextCode
                autoSave={this.autoSave}
                codeMode={model.codeMode}
                compilationMode={variant === 'code-compiler'}
                context={context}
                correctResponses={correctResponses}
                defaultValue={defaultValue}
                extraResponses={extraResponses}
                handleInteractionReady={handleInteractionReady}
                isPreRender={isPreRender}
                isReadOnly={isReadOnly}
                isViewMediaPrint={isViewMediaPrint}
                model={model}
                questionModel={questionModel}
                services={services}
                shouldSaveOnEmptyChange={this.shouldSaveOnEmptyChange()}
                showGutter={model.showGutter}
                userData={userData}
                viewMedia={viewMedia}
                ref={(component) => {
                    this.editor = component;
                }}
            />
        );
    }

    renderExtendedTextCkeditor() {
        const { handleInteractionReady, isPreRender, isReadOnly, model, questionModel, userResponse, context, services, viewMedia } = this.props;
        const userData = getUserData(userResponse);

        if (this.shouldHideInteractionsNotUsefulOnPaper()) {
            return null;
        }

        return (
            <ExtendedTextCkeditor
                autoExpand={model.autoExpand}
                autoSave={this.autoSave}
                context={context}
                expectedLines={model.expectedLines}
                handleInteractionReady={handleInteractionReady}
                isPreRender={isPreRender}
                isReadOnly={isReadOnly}
                maxWords={model.maxWords}
                model={model}
                questionModel={questionModel}
                services={services}
                shouldSaveOnEmptyChange={this.shouldSaveOnEmptyChange()}
                showWordCount={wordCountService.shouldShowWordCount(model.showWordCount)}
                store={storeManager.getStore()}
                userData={userData}
                viewMedia={viewMedia}
                ref={(component) => {
                    this.editor = component ? component.getWrappedInstance() : undefined;
                }}
            />
        );
    }

    render() {
        const { model, services, settingValues, isReadOnly, questionModel } = this.props;
        const isPaperBased = sessionReader.isPrintStylePaperBased();
        let interaction;

        // Get accessibility attributes for the main question group
        const questionGroupA11yAttrs = extendedTextA11ySelector.getQuestionGroupA11yAttributes(model, questionModel);
        const richTextAnswerGroupA11yAttrs = extendedTextA11ySelector.getRichTextAnswerGroupA11yAttributes(model, questionModel);
        const keyboardInstructions = extendedTextA11ySelector.getKeyboardInstructions();

        if (model.format === 'plain') {
            interaction = this.renderExtendedTextPlainText();
        } else if (isCodeInteraction(model)) {
            interaction = this.renderExtendedTextCode(model.variant);
        } else {
            interaction = this.renderExtendedTextCkeditor();
        }

        return (
            <div
                className="interaction allowed-break-inside"
                role={questionGroupA11yAttrs.role}
                aria-label={questionGroupA11yAttrs.ariaLabel}
            >
                {
                    isRichTextInteraction(model) ?
                        <KeyboardHelpAndPrompt
                            captionKey="interactions.extendedTextInteraction.keyboardInteractionHelpText"
                            translationService={services.translationService}
                            model={model}
                            showAccessibilityHelpers={settingValues.show_accessibility_helpers && !isReadOnly}
                            questionModel={questionModel}
                        />
                        : (!isPaperBased && <Prompt model={model.prompt} />)
                }
                <br className="tts-hidden-break" />
                {keyboardInstructions && (
                    <div
                        role={richTextAnswerGroupA11yAttrs.role}
                        aria-label={richTextAnswerGroupA11yAttrs.ariaLabel}
                    >
                        {interaction}
                    </div>
                )}
                {!keyboardInstructions && interaction}
            </div>
        );
    }
}

export default ExtendedTextInteraction;
